import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import useResilientWebSocket from "./hooks/useResilientWebSocket";
import "./Feed.scss";

export default function Feed({ token, user, onSignOut }) {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const audioRef = useRef(null);

  const apiBase = process.env.REACT_APP_API_URL;
  const wsBase = process.env.REACT_APP_WS_URL;

  //
  // 1. Load historical feed
  //
  useEffect(() => {
    if (!token) return;

    async function loadHistory() {
      try {
        const res = await fetch(`${apiBase}/api/news/recent?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          console.warn("History fetch failed:", res.status);
          return;
        }

        const rows = await res.json();

        const mapped = rows.flatMap((row) => {
          const items = [];

          items.push({
            type: "raw",
            description: row.content,
            summary: null,
            timestamp: row.received_at,
            id: row.feed_id,
          });

          if (row.ai_summary) {
            items.push({
              type: "summary",
              description: null,
              summary: row.ai_summary,
              timestamp: row.summarized_at || row.received_at,
              id: row.feed_id,
            });
          }

          return items;
        });

        setMessages(mapped);
      } catch (err) {
        console.warn("History fetch error:", err);
      }
    }

    loadHistory();
  }, [token, apiBase]);

  //
  // Helper: dedupe messages
  //
  function addMessageDeduped(newMsg) {
    setMessages((prev) => {
      const exists = prev.some(
        (m) => m.id === newMsg.id && m.type === newMsg.type
      );
      if (exists) return prev;
      return [newMsg, ...prev];
    });
  }

  //
  // Helper: send notification via Service Worker
  //
  function sendToServiceWorkerForNotification(title, body) {
    if (!("serviceWorker" in navigator)) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    navigator.serviceWorker.ready.then((registration) => {
      if (!registration.active) return;

      registration.active.postMessage({
        type: "NEW_FEED_ITEM",
        payload: { title, body },
      });
    });
  }

  //
  // User-triggered notification permission
  //
  function requestNotificationPermission() {
    if (!("Notification" in window)) {
      alert("Notifications are not supported in this browser.");
      return;
    }

    if (typeof Notification === "undefined") {
      console.warn("Notifications not supported on this device.");
      return;
    }

    Notification.requestPermission().then((result) => {

      console.log("Notification permission:", result);
      setNotificationPermission(result);
    });
  }

  //
  // 2. WebSocket live feed
  //
  const connectionStatus = useResilientWebSocket(wsBase, token, (msg) => {
    if (msg.type === "raw") {
      const rawItem = {
        type: "raw",
        description: msg.data.content,
        summary: null,
        timestamp: msg.data.received_at || new Date(),
        id: msg.data.id,
      };

      // Play sound in tab
      if (soundEnabled && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }

      // Strip HTML for notification body
      const plainText =
        msg.data.content?.replace(/<[^>]+>/g, "") || "New feed item";

      // Notify via Service Worker
      sendToServiceWorkerForNotification("New Feed Item", plainText);

      addMessageDeduped(rawItem);
    }

    if (msg.type === "summary") {
      const summaryItem = {
        type: "summary",
        description: null,
        summary: msg.data.summary,
        timestamp: new Date(),
        id: msg.data.id,
      };

      addMessageDeduped(summaryItem);
    }
  });

  //
  // 3. Filtering
  //
  const filteredMessages = messages.filter((msg) => {
    if (filter === "raw") return msg.type === "raw";
    if (filter === "ai") return msg.type === "summary";
    return true;
  });

  //
  // 4. HTML rendering helper
  //
  const renderHtml = (html) => {
    const clean = DOMPurify.sanitize(html);
    return <div dangerouslySetInnerHTML={{ __html: clean }} />;
  };

  //
  // 5. Connection status label + class
  //
  const statusLabel = {
    connected: "Connected",
    connecting: "Connecting…",
    reconnecting: "Reconnecting…",
    disconnected: "Disconnected",
  }[connectionStatus];

  const statusClass = {
    connected: "status-connected",
    connecting: "status-reconnecting",
    reconnecting: "status-reconnecting",
    disconnected: "status-disconnected",
  }[connectionStatus];

  return (
    <div className="feed-container">
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />

      <div className="feed-header">
        <div className="controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="raw">Raw Only</option>
            <option value="ai">AI Summaries Only</option>
          </select>

          <button
            className="sound-btn"
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? "🔊 Sound On" : "🔇 Sound Off"}
          </button>

          {notificationPermission !== "granted" && (
            <button
              className="sound-btn"
              onClick={requestNotificationPermission}
              style={{ background: "#2196f3", color: "white" }}
            >
              Enable Notifications
            </button>
          )}
        </div>

        <div className="profile">
          <div className="email">{user?.profile?.email}</div>

          <div className="connection-status">
            <span className={`status-dot ${statusClass}`}></span>
            {statusLabel}
          </div>

          <button className="signout-btn" onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      </div>

      {filteredMessages.map((msg, index) => (
        <div
          key={index}
          className={`feed-card ${msg.type === "raw" ? "raw" : "ai"}`}
        >
          <div className="timestamp">
            {new Date(msg.timestamp).toLocaleString()}
          </div>

          {msg.type === "raw" && (
            <div className="raw-text">
              <strong>Raw:</strong>
              {renderHtml(msg.description)}
            </div>
          )}

          {msg.type === "summary" && (
            <div className="summary">
              <strong>AI Summary:</strong>
              {renderHtml(msg.summary)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}